/**
 * Design system icon (`Icon` in the BuiltSimple Design System).
 *
 * BS Labs draws icons with real Phosphor Regular glyphs (filled outlines,
 * `fill: currentColor`). This used to carry hand-drawn stroke approximations
 * of them; the names below now resolve to the real Phosphor SVGs from
 * @phosphor-icons/core, so screens built on DsIcon match the rest of the app,
 * whose mdi and tabler icons are drawn from the same set (see src/icons).
 *
 * `dsIcon(name)` returns a component that takes the same `size` / `stroke-width`
 * props as vue-tabler-icons, so `<component :is="item.icon" size="16" />` call
 * sites work unchanged when an icon is swapped. stroke-width is accepted and
 * ignored: Phosphor Regular has one fixed weight.
 */
import { defineComponent, h, type Component } from 'vue';
import { phosphorSvg } from '@/icons/phosphor';

/** DsIcon name -> Phosphor Regular name. Keep the DsIcon names stable: call sites use them. */
export const DS_TO_PHOSPHOR: Record<string, string> = {
    check: 'check',
    clock: 'clock',
    plus: 'plus',
    search: 'magnifying-glass',
    pencil: 'pencil-simple',
    trash: 'trash',
    calendar: 'calendar-blank',
    users: 'users',
    ruler: 'ruler',
    coins: 'coins',
    receipt: 'receipt',
    truck: 'truck',
    home: 'house',
    buildings: 'buildings',
    calc: 'calculator',
    shield: 'shield-check',
    bell: 'bell',
    file: 'file-text',
    warn: 'warning',
    info: 'info',
    lock: 'lock',
    'caret-down': 'caret-down',
    'caret-right': 'caret-right',
    'arrow-left': 'arrow-left',
    minus: 'minus',
    plans: 'map-trifold',
    swatch: 'swatches',
    eye: 'eye',
    copy: 'copy',
    upload: 'upload-simple',
    send: 'paper-plane-tilt',
    download: 'download-simple',
    sparkle: 'sparkle',
    rocket: 'rocket',
    help: 'question',
    dots: 'dots-three',
    wrench: 'wrench',
    list: 'list',
    x: 'x',
    user: 'user',
    sliders: 'sliders-horizontal',
    'sign-out': 'sign-out',
    'caret-up': 'caret-up',
    cursor: 'cursor',
    polygon: 'polygon',
    line: 'line-segment',
    count: 'circles-three',
    undo: 'arrow-u-up-left',
    redo: 'arrow-u-up-right',
    hand: 'hand',
    keyboard: 'keyboard',
    rectangle: 'rectangle',
    magnet: 'magnet',
    'arrows-out': 'arrows-out',
    cube: 'cube',
    'eye-off': 'eye-slash',
    // BS Labs main navigation: area icons (18) and menu-row icons (16).
    house: 'house',
    calculator: 'calculator',
    briefcase: 'briefcase',
    'presentation-chart': 'presentation-chart',
    toolbox: 'toolbox',
    'user-gear': 'user-gear',
    'user-check': 'user-check',
    'clipboard-text': 'clipboard-text',
    bulldozer: 'bulldozer',
    'chat-circle-text': 'chat-circle-text',
    'book-open': 'book-open',
    folder: 'folder',
    'note-pencil': 'note-pencil',
    'lock-simple': 'lock-simple',
    code: 'code',
    'hourglass-medium': 'hourglass-medium',
    'magnifying-glass': 'magnifying-glass',
    question: 'question',
    // Record header and Estimate overview.
    'caret-left': 'caret-left',
    'arrow-right': 'arrow-right',
    'dots-vertical': 'dots-three-vertical',
    link: 'link-simple',
    'check-circle': 'check-circle',
    'file-image': 'file-image',
    'file-pdf': 'file-pdf',
    'file-doc': 'file-doc',
    'file-xls': 'file-xls',
    'arrow-square-out': 'arrow-square-out',
    'currency-dollar': 'currency-dollar',
    // Dashboard: weather glyphs, Weather location select, links and inline status.
    sun: 'sun',
    cloud: 'cloud',
    'cloud-sun': 'cloud-sun',
    'cloud-rain': 'cloud-rain',
    'cloud-lightning': 'cloud-lightning',
    'cloud-snow': 'cloud-snow',
    'cloud-fog': 'cloud-fog',
    'cloud-slash': 'cloud-slash',
    'map-pin': 'map-pin',
    'chart-line-down': 'chart-line-down',
    'calendar-blank': 'calendar-blank',
    'file-dashed': 'file-dashed',
    // Dashboard widgets (part B): activity rows and week navigator.
    'calendar-plus': 'calendar-plus',
    'calendar-check': 'calendar-check',
    // Schedules header and toolbar (Figma 206:1743 / 206:1525): Broadcast, Storage tab, Gantt segment.
    'megaphone-simple': 'megaphone-simple',
    tray: 'tray',
    'chart-bar-horizontal': 'chart-bar-horizontal',
    // Estimate Scope hub and Takeoff table (Figma 7:10954 / 7:12704): measurement
    // type tiles (area, count) and Open takeoff editor.
    square: 'square',
    hash: 'hash',
    'corners-out': 'corners-out',
    // Takeoff editor: panel toggle, drag handle, markup tools, openings.
    sidebar: 'sidebar-simple',
    grip: 'dots-six-vertical',
    'arrow-up-right': 'arrow-up-right',
    'text-t': 'text-t',
    callout: 'chat-teardrop',
    scissors: 'scissors',
    pentagon: 'pentagon',
    'folder-move': 'folder-simple-dashed',
    // Estimates list/table view switcher (Figma 77:33401).
    table: 'table',
    // Form drawer (Figma 286:823): the error summary's icon.
    'warning-circle': 'warning-circle',
    // Quote documents (Figma 269:40776): an in-progress Customise section.
    'circle-notch': 'circle-notch',
    // Projects list and Add project dialog (Figma 306:36412, 306:37237).
    'user-plus': 'user-plus',
    notebook: 'notebook',
    'arrow-counter-clockwise': 'arrow-counter-clockwise',
    funnel: 'funnel-simple',
    // Plans & takeoff (Figma 7:10776): fit-to-sheet and the area measurement tile.
    // Quotes list, record and document toolbar (Figma 306:37484).
    'chart-bar': 'chart-bar',
    'arrow-down': 'arrow-down',
    'arrow-up': 'arrow-up',
    'caret-up-down': 'caret-up-down',
    export: 'export',
    history: 'clock-counter-clockwise',
    'circle-dashed': 'circle-dashed',
    image: 'image',
    refresh: 'arrows-clockwise',
    'chat-circle': 'chat-circle',
    'squares-four': 'squares-four'
};

export const DS_ICON_NAMES = Object.keys(DS_TO_PHOSPHOR);

export const DsIcon = defineComponent({
    name: 'DsIcon',
    props: {
        name: { type: String, required: true },
        size: { type: [Number, String], default: 16 },
        strokeWidth: { type: [Number, String], default: undefined }
    },
    setup(props) {
        // An unknown name draws an empty box of the right size, as
        // before, rather than throwing, so a typo never breaks a whole row.
        return () =>
            phosphorSvg(DS_TO_PHOSPHOR[props.name] ?? '', { size: props.size, class: 'ds-icon' }) ??
            h('svg', { class: 'ds-icon', width: props.size, height: props.size, 'aria-hidden': 'true' });
    }
});

const cache = new Map<string, Component>();

/** A component for one icon, prop-compatible with vue-tabler-icons. */
export function dsIcon(name: string): Component {
    let c = cache.get(name);
    if (!c) {
        c = defineComponent({
            name: `DsIcon-${name}`,
            props: { size: { type: [Number, String], default: 16 }, strokeWidth: { type: [Number, String], default: undefined } },
            setup: (p) => () => h(DsIcon, { name, size: p.size })
        });
        cache.set(name, c);
    }
    return c;
}
